import type { ReactNode } from 'react';
import Link from 'next/link';
import { resolveNavHref, signupDialogIntent } from '@/lib/signup';
import type { NavLink } from '@/lib/types';

interface NavAnchorProps {
  link: NavLink;
  className?: string;
  current?: boolean;
  onClick?: () => void;
  /**
   * true: a signup link that has a page route opens the registration dialog
   * (homepage only, as on canideal.org). Default: it links to that page.
   */
  dialog?: boolean;
  /** Content instead of the link label (e.g. label plus an arrow). */
  children?: ReactNode;
}

/**
 * Renders any shared nav/footer link: internal routes use Next Link, signup
 * links carry `data-cid-signup` so the global SignupDialog can open them.
 */
export function NavAnchor({
  link,
  className,
  current,
  onClick,
  dialog = false,
  children,
}: NavAnchorProps) {
  const content = children ?? link.label;
  const href = resolveNavHref(link);
  const ariaCurrent = current ? ('page' as const) : undefined;
  const intent = signupDialogIntent(link, dialog);

  if (intent) {
    return (
      <a href={href} className={className} data-cid-signup={intent} onClick={onClick}>
        {content}
      </a>
    );
  }
  if (/^https?:/.test(href)) {
    return (
      <a href={href} className={className} onClick={onClick}>
        {content}
      </a>
    );
  }
  return (
    <Link href={href} className={className} aria-current={ariaCurrent} onClick={onClick}>
      {content}
    </Link>
  );
}
